import { action, atom, noop, onConnect } from '@reatom/framework'
import i18next from 'i18next'

import { pipe, Result } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import { Comment, CommentReactions } from '@root/entities/comment/model'
import { CommentId } from '@root/entities/comment/model/comment-id'
import { Network } from '@root/entities/network/model'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { TypeHelper } from '@blade/std-lib'
import type { CurrencyAddress } from '@root/entities/currency/model'
import type { PairAddress } from '@root/entities/pair/model/PairAddress'
import type { CommentApi } from '@root/shared/api'

type CommentsThreadMode = 'dynamic' | 'static'

export const commentsScrollEvent = action(noop, 'commentsScrollEvent')

export const commentsUpdatedTotalEvent = action<[number]>(
  noop,
  'commentsUpdatedTotalEvent',
)

export const fetchCommentsRequestAction = action(
  async (ctx, req: CommentApi.GetCommentsReqDto) => {
    const { tradeApiClient, environment } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.comment.activityCommentsList, [
        {
          ...req,
          depth: Comment.COMMENT_DEPTH,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.map((it) =>
        Comment.threadFromDto(it, {
          avatarHost: environment.assets.avatarsHost,
        }),
      ),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'fetchCommentsRequestAction',
)

export interface CreateCommentRequestActionOptions {
  pairAddress: PairAddress
  req: CommentApi.CreateCommentReqDto
}

export const createCommentRequestAction = action(
  async (ctx, { pairAddress, req }: CreateCommentRequestActionOptions) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const user = ctx.get(userAtom)

    if (!user) {
      throw new Error('User not found')
    }

    const base = {
      ...req,
    } satisfies Partial<CommentApi.CreateCommentReqDto>

    const result = await ctx.schedule(() =>
      // Positional: the route carries no query parameters, so the generator
      // writes the path's own values as plain arguments.
      apiRequest(ctx, tradeApiClient.comment.activityCommentCreateCreate, [
        pairAddress,
        base,
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.map((it) =>
        Comment.commentFromRespDto(pairAddress, user, req, it),
      ),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'createCommentRequestAction',
)

interface DeleteCommentRequest {
  commentId: CommentId
}

export const deleteCommentRequestAction = action(
  async (
    ctx,
    {
      pairAddress,
      req,
    }: { pairAddress: PairAddress; req: DeleteCommentRequest },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.comment.activityCommentDeleteCreate, [
        pairAddress,
        req,
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.map((x) => ({
        commentId: CommentId(x.commentId),
      })),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'deleteCommentRequestAction',
)

interface EditCommentReq
  extends TypeHelper.OmitKey<CommentApi.EditCommentReqDto, 'commentId'> {
  commentId: CommentId
}

export const editCommentRequestAction = action(
  async (ctx, { req }: { req: EditCommentReq }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.comment.activityCommentEditCreate, [req]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'editCommentRequestAction',
)

export const toggleCommentReactionRequestAction = action(
  async (
    ctx,
    {
      pairAddress,
      commentId,
      reaction,
    }: {
      pairAddress: PairAddress
      commentId: CommentId
      reaction: CommentReactions.Reaction
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const user = ctx.get(userAtom)

    if (!user) {
      throw new Error('User not found')
    }

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.comment.activityCommentToggleReactionCreate,
        [
          pairAddress,
          commentId,
          {
            reactionType: reaction,
          },
        ],
      ),
    )

    const mappedResult = pipe(
      result,
      Result.map(() => ({
        reaction,
        commentId,
        userId: user.delugeId,
      })),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'toggleCommentReactionRequestAction',
)

type CommentsArgs = {
  pairAddress: PairAddress
  quoteTokenAddress: CurrencyAddress
  baseTokenAddress: CurrencyAddress
  network: Network.Network
  threadInitial?: Array<Comment.Comment>
  threadMode?: CommentsThreadMode
}

export const reatomComments = ({
  // TODO: place with pair: Pair.Pair
  pairAddress,
  quoteTokenAddress,
  baseTokenAddress,
  network,
  threadInitial,
  threadMode = 'static',
}: CommentsArgs) => {
  const commentsThreadModeAtom = atom<CommentsThreadMode>(
    threadMode,
    'commentsThreadModeAtom',
  )
  const commentsThreadAtom = atom<Comment.CommentsThread>(
    {
      comments: threadInitial ?? [],
      totalCount: threadInitial?.length ?? 0,
    },
    'commentsThreadAtom',
  )
  const replyCommentIdAtom = atom<CommentId | 'root' | undefined>(
    undefined,
    'replyCommentIdAtom',
  )
  const editCommentIdAtom = atom<CommentId | undefined>(
    undefined,
    'editCommentIdAtom',
  )
  const commentCreatedEvent = action<[Comment.Comment, number]>(
    noop,
    'commentCreatedEvent',
  )
  const {
    executeAction: loadCommentsAction,
    requestStateAtom: loadCommentsRequestState,
  } = reatomRequest({
    execAction: fetchCommentsRequestAction,
    name: `loadCommentsRequest_${pairAddress}_${network}`,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      commentsThreadAtom(ctx, result.success)
      commentsUpdatedTotalEvent(ctx, result.success.totalCount)
    },
  })
  const {
    executeAction: createCommentAction,
    requestStateAtom: createCommentRequestState,
  } = reatomRequest({
    execAction: createCommentRequestAction,
    prepare: (
      _,
      args: {
        pairAddress: PairAddress
        req: TypeHelper.OmitKey<CommentApi.CreateCommentReqDto, 'pairInfo'>
      },
    ) => {
      return {
        ...args,
        req: {
          ...args.req,
          pairInfo: {
            baseTokenAddress,
            quoteTokenAddress,
          },
        },
      }
    },
    name: `createCommentRequest_${pairAddress}_${network}`,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      const newComment = result.success
      const commentsAggregated = ctx.get(commentsThreadAtom)
      const thread = commentsAggregated.comments
      const threadUpdated = Comment.threadInsertReplies(thread, newComment)
      const totalCount = commentsAggregated.totalCount + 1

      commentsThreadAtom(ctx, {
        comments: [...threadUpdated],
        totalCount,
      })
      commentCreatedEvent(ctx, newComment, totalCount)
      commentsUpdatedTotalEvent(ctx, totalCount)

      showSnackbarAction(ctx, {
        text: i18next.t('comment.comment_created'),
        intent: 'success',
      })
    },
  })
  const {
    executeAction: editCommentAction,
    requestStateAtom: editCommentRequestState,
  } = reatomRequest({
    execAction: editCommentRequestAction,
    name: `editCommentRequest_${pairAddress}_${network}`,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      const commentsAggregated = ctx.get(commentsThreadAtom)
      const thread = commentsAggregated.comments
      const threadUpdated = Comment.threadUpdateComment(
        thread,
        result.success as Comment.Comment,
      )

      commentsThreadAtom(ctx, {
        comments: [...threadUpdated],
        totalCount: commentsAggregated.totalCount,
      })
      editCommentIdAtom(ctx, undefined)
      showSnackbarAction(ctx, {
        text: i18next.t('comment.comment_edited'),
        intent: 'success',
      })
    },
  })

  const deleteCommentLocallyAction = action((ctx, commentId: CommentId) => {
    const commentsAggregated = ctx.get(commentsThreadAtom)
    const thread = commentsAggregated.comments
    const threadUpdated = Comment.threadDeleteComment(thread, commentId)
    const totalCount = commentsAggregated.totalCount - 1

    commentsThreadAtom(ctx, {
      comments: [...threadUpdated],
      totalCount,
    })
    commentsUpdatedTotalEvent(ctx, totalCount)
  }, 'deleteCommentLocallyAction')

  const {
    executeAction: deleteCommentAction,
    requestStateAtom: deleteCommentRequestState,
  } = reatomRequest({
    execAction: deleteCommentRequestAction,
    name: `deleteCommentRequest_${pairAddress}_${network}`,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      deleteCommentLocallyAction(ctx, result.success.commentId)

      showSnackbarAction(ctx, {
        text: i18next.t('comment.comment_deleted'),
        intent: 'success',
      })
    },
  })
  const {
    executeAction: toggleReactionCommentAction,
    requestStateAtom: toggleReactionCommentRequestState,
  } = reatomRequest({
    execAction: toggleCommentReactionRequestAction,
    name: `toggleCommentReactionRequest_${pairAddress}_${network}`,
    onResult: (ctx, result) => {
      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      const commentsAggregated = ctx.get(commentsThreadAtom)
      const thread = commentsAggregated.comments
      const totalCount = commentsAggregated.totalCount

      const threadUpdated = CommentReactions.updateThreadReactions(
        thread,
        result.success,
      )

      commentsThreadAtom(ctx, {
        comments: [...threadUpdated],
        totalCount,
      })
    },
  })

  if (threadInitial === undefined) {
    onConnect(loadCommentsRequestState, (ctx) => {
      loadCommentsAction(ctx, {
        pairAddress,
        network: Network.toDto(network),
      })
    })
  }
  const loadRepliesThreadAction = action((ctx) => {
    if (threadInitial?.length === 0) {
      showSnackbarAction(ctx, {
        text: "Can't be used without initial comments",
        intent: 'error',
      })
    }

    loadCommentsAction(ctx, {
      pairAddress,
      network: Network.toDto(network),
      commentId: threadInitial?.[0]?.id,
    })
  }, 'loadRepliesThreadAction')

  return {
    commentsThreadAtom,
    commentsThreadModeAtom,
    replyCommentIdAtom,
    editCommentIdAtom,
    commentCreatedEvent,

    loadCommentsAction,
    loadRepliesThreadAction,
    loadCommentsRequestState,
    createCommentAction,
    createCommentRequestState,
    editCommentAction,
    editCommentRequestState,
    deleteCommentAction,
    deleteCommentLocallyAction,
    deleteCommentRequestState,
    toggleReactionCommentAction,
    toggleReactionCommentRequestState,
  }
}

export type CommentsModel = ReturnType<typeof reatomComments>
